/**
 * 语音管理Store (Pinia)
 * 管理语音数据、分类、收藏等功能
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { voiceApi, type VoiceResponse } from '@/api/voice';
import { ElMessage } from 'element-plus';
import WebStore from './web-store';
import { favoritesApi } from '@/api/favorites';
import { useUserStore } from './user';
import { voiceFilter, LanguageType} from '@/utils/voiceFilter';

export const useVoiceStore = defineStore('voice', () => {
  // 本地存储实例
  const webStore = new WebStore();

  // 缓存配置 - 按语言类型分别缓存
  const getCacheKeys = (languageType: LanguageType) => ({
    VOICES_DATA: `voices_cache_data_${languageType}`,
    CACHE_TIMESTAMP: `voices_cache_timestamp_${languageType}`,
    CACHE_VERSION: `voices_cache_version_${languageType}`
  });

  // 缓存有效期：30分钟（毫秒）- 增加缓存时间，减少不必要的请求
  const CACHE_DURATION = 30 * 60 * 1000;
  const CACHE_VERSION = '2.2.0'; // 更新版本号，支持按语言类型分别缓存

  // ============= 状态定义 =============
  const voices = ref<VoiceResponse[]>([]);
  const favorites = ref<VoiceResponse[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // 语言类型和过滤状态
  const currentLanguageType = ref<LanguageType>(LanguageType.CHINESE);
  const currentTags = ref<string[]>([]);
  const selectedTags = ref<string[]>([]);
  const selectedDialect = ref<string>('');
  const selectedLocale = ref<string>('');
  const searchKeyword = ref<string>('');

  // ============= 计算属性 =============
  const voiceMap = computed(() => {
    const map = new Map<string, VoiceResponse>();
    voices.value.forEach(voice => {
      map.set(voice.shortName, voice);
    });
    return map;
  });

  const favoriteVoiceIds = computed(() => {
    return new Set(favorites.value.map(voice => voice.id));
  });

  const voiceCount = computed(() => voices.value.length);
  const favoriteCount = computed(() => favorites.value.length);

  // 当前过滤后的语音列表
  const filteredVoices = computed(() => {
    // 初始化过滤器
    voiceFilter.setVoices(voices.value);

    const filterOptions = {
      dialect: selectedDialect.value,
      locale: selectedLocale.value,
      tags: selectedTags.value,
      keyword: searchKeyword.value
    };

    return voiceFilter.filter(filterOptions);
  });

  // 当前语言类型的可用选项（方言或国家）
  const availableOptions = computed(() => {
    // 确保过滤器有最新的语音数据
    voiceFilter.setVoices(voices.value);
    return voiceFilter.getAvailableOptions(currentLanguageType.value);
  });

  // 动态标签：基于当前筛选条件的可用标签
  const availableTags = computed(() => {
    // 获取当前筛选后的语音列表
    const currentVoices = filteredVoices.value;

    // 提取所有标签并统计数量
    const tagCounts = new Map<string, number>();

    currentVoices.forEach(voice => {
      if (voice.tags && Array.isArray(voice.tags)) {
        voice.tags.forEach(tag => {
          if (typeof tag === 'string' && tag.trim()) {
            tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
          }
        });
      }
    });

    // 转换为带数量的标签对象数组，按数量降序排列
    return Array.from(tagCounts.entries())
      .map(([tag, count]) => ({ name: tag, count }))
      .sort((a, b) => b.count - a.count);
  });

  // ============= Actions =============
  
  /**
   * 检查是否收藏
   */
  const isFavorite = (voiceId: number) => {
    return favoriteVoiceIds.value.has(voiceId);
  };

  /**
   * 获取指定语言类型的语音数据
   */
  const fetchVoicesByLanguageType = async (languageType: LanguageType, forceRefresh = false) => {
    if (loading.value) {
      console.log('⏳ [VoiceStore] 请求进行中，跳过');
      return;
    }

    try {
      loading.value = true;
      error.value = null;

      // 如果不是强制刷新，先检查缓存
      if (!forceRefresh && isCacheValid(languageType)) {
        const cachedData = getCachedData(languageType);
        if (cachedData && cachedData.voices && cachedData.voices.length > 0) {
          console.log(`📦 [VoiceStore] 缓存命中 ${languageType}: ${cachedData.voices.length} 个语音`);

          // 更新当前语言类型的数据
          currentLanguageType.value = languageType;
          voices.value = cachedData.voices;
          currentTags.value = cachedData.tags || [];

          // 初始化过滤器
          voiceFilter.setVoices(voices.value);

          loading.value = false;
          return;
        }
      }

      // 获取对应的类型参数
      const typeParam = getTypeStringFromLanguageType(languageType);

      // 如果是强制刷新，清空所有旧数据
      if (forceRefresh) {
        voices.value = [];
      }

      console.log(`🌐 [VoiceStore] 加载 ${languageType} 数据...`);

      // 调用简化的API
      const response = await voiceApi.getVoicesByType(typeParam as '1' | '2' | '3', forceRefresh);

      if (response && response.voices) {
        console.log(`✅ [VoiceStore] ${languageType} 加载完成: ${response.voices.length} 个语音`);

        // 更新当前语言类型的数据
        currentLanguageType.value = languageType;

        // 将 Tag 对象数组转换为字符串数组
        currentTags.value = response.tagList ? response.tagList.map(tag => tag.name) : [];



        // 创建标签ID到名称的映射
        const tagIdToName = new Map<number, string>();
        if (response.tagList) {
          response.tagList.forEach(tag => {
            tagIdToName.set(tag.id, tag.name);
          });
        }

        // 处理语音数据，将 tags 从数字数组转换为字符串数组
        const processedVoices = response.voices.map(voice => ({
          ...voice,
          tags: Array.isArray(voice.tags)
            ? voice.tags.map(tagId => {
                if (typeof tagId === 'number') {
                  return tagIdToName.get(tagId) || `Tag${tagId}`;
                }
                return tagId; // 如果已经是字符串，直接返回
              })
            : []
        }));

        // 直接替换为当前语言类型的语音数据
        voices.value = processedVoices;

        // 初始化过滤器
        voiceFilter.setVoices(voices.value);

        // 缓存当前语言类型的数据
        cacheData(languageType, {
          voices: voices.value,
          tags: currentTags.value,
          languageType: languageType,
          timestamp: Date.now()
        });
      } else {
        console.warn(`⚠️ [VoiceStore] API 响应无效或缺少 voices 数据:`, response);
      }
    } catch (err: any) {
      console.error(`❌ [VoiceStore] 加载 ${languageType} 失败:`, err);
      error.value = err.message || `获取 ${languageType} 语音数据失败`;
      ElMessage({ type: 'error', message: error.value || '获取语音数据失败' });
    } finally {
      loading.value = false;
    }
  };

  /**
   * 获取所有语音数据 - 现在只是加载当前语言类型的数据
   */
  const fetchVoices = async (forceRefresh = false) => {
    // 直接加载当前语言类型的数据
    await fetchVoicesByLanguageType(currentLanguageType.value, forceRefresh);
  };

  // ============= 新的状态管理方法 =============

  /**
   * 切换语言类型
   */
  const setLanguageType = async (languageType: LanguageType, forceRefresh = false) => {
    console.log(`🔄 [VoiceStore] setLanguageType: ${currentLanguageType.value} → ${languageType}`);

    // 如果是相同语言类型且不强制刷新，直接返回
    if (currentLanguageType.value === languageType && !forceRefresh) {
      console.log(`⏭️ [VoiceStore] 相同语言类型，跳过`);
      return;
    }

    // 重置选择状态
    selectedTags.value = [];
    selectedDialect.value = '';
    selectedLocale.value = '';
    searchKeyword.value = '';

    // 获取对应语言类型的数据
    await fetchVoicesByLanguageType(languageType, forceRefresh);
  };

  /**
   * 设置选中的标签
   */
  const setSelectedTags = (tags: string[]) => {
    selectedTags.value = tags;
  };

  /**
   * 设置选中的方言（仅中文）
   */
  const setSelectedDialect = (dialect: string) => {
    selectedDialect.value = dialect;
  };

  /**
   * 设置选中的国家/地区（仅其他语言）
   */
  const setSelectedLocale = (locale: string) => {
    selectedLocale.value = locale;
  };

  /**
   * 设置搜索关键词
   */
  const setSearchKeyword = (keyword: string) => {
    searchKeyword.value = keyword;
  };

  /**
   * 清除所有过滤条件
   */
  const clearFilters = () => {
    selectedTags.value = [];
    selectedDialect.value = '';
    selectedLocale.value = '';
    searchKeyword.value = '';
  };

  /**
   * 获取当前筛选条件的面包屑导航
   */
  const getFilterBreadcrumbs = computed(() => {
    const breadcrumbs: Array<{ type: string; label: string; value: string }> = [];

    // 语言类型
    if (currentLanguageType.value) {
      const languageLabels = {
        [LanguageType.CHINESE]: '中文',
        [LanguageType.ENGLISH]: '英文',
        [LanguageType.OTHER]: '其他语言'
      };
      breadcrumbs.push({
        type: 'language',
        label: languageLabels[currentLanguageType.value] || currentLanguageType.value,
        value: currentLanguageType.value
      });
    }

    // 方言
    if (selectedDialect.value) {
      breadcrumbs.push({
        type: 'dialect',
        label: selectedDialect.value,
        value: selectedDialect.value
      });
    }

    // 国家/地区
    if (selectedLocale.value) {
      breadcrumbs.push({
        type: 'locale',
        label: selectedLocale.value,
        value: selectedLocale.value
      });
    }

    // 标签
    selectedTags.value.forEach(tag => {
      breadcrumbs.push({
        type: 'tag',
        label: tag,
        value: tag
      });
    });

    // 搜索关键词
    if (searchKeyword.value) {
      breadcrumbs.push({
        type: 'search',
        label: `搜索: ${searchKeyword.value}`,
        value: searchKeyword.value
      });
    }

    return breadcrumbs;
  });

  /**
   * 移除特定的筛选条件
   */
  const removeFilter = (type: string, value: string) => {
    switch (type) {
      case 'dialect':
        selectedDialect.value = '';
        break;
      case 'locale':
        selectedLocale.value = '';
        break;
      case 'tag':
        selectedTags.value = selectedTags.value.filter(tag => tag !== value);
        break;
      case 'search':
        searchKeyword.value = '';
        break;
    }
  };

  // ============= 辅助函数 =============
  /**
   * 将 LanguageType 枚举转换为类型字符串
   */
  const getTypeStringFromLanguageType = (languageType: LanguageType): string => {
    switch (languageType) {
      case LanguageType.CHINESE:
        return '1';
      case LanguageType.ENGLISH:
        return '2';
      case LanguageType.OTHER:
        return '3';
      default:
        return '1';
    }
  };

  /**
   * 搜索语音
   */
  const searchVoices = (keyword: string, filters?: {
    language?: string;
    gender?: string;
    category?: string;
  }) => {
    let result = voices.value;

    // 关键词搜索
    if (keyword) {
      const lowerKeyword = keyword.toLowerCase();
      result = result.filter(voice => 
        (voice.name || voice.localName || '').toLowerCase().includes(lowerKeyword) ||
        voice.shortName.toLowerCase().includes(lowerKeyword) ||
        voice.locale.toLowerCase().includes(lowerKeyword)
      );
    }

    // 过滤条件
    if (filters) {
      if (filters.language) {
        result = result.filter(voice => voice.locale === filters.language);
      }
      if (filters.gender) {
        result = result.filter(voice => voice.gender === filters.gender);
      }
      if (filters.category) {
        result = result.filter(voice => (voice as any).category === filters.category);
      }
    }

    return result;
  };

  /**
   * 添加到收藏
   * 根据登录状态决定是本地收藏还是后端同步
   */
  const addToFavorites = async (voice: VoiceResponse) => {
    try {
      if (isFavorite(voice.id)) {
        return; // 已经收藏
      }

      const userStore = useUserStore();

      // 如果已登录，调用后端API
      if (userStore.isLoggedIn) {
        await favoritesApi.addVoice(voice.id);
      }

      // 更新本地状态（无论是否登录都更新）
      favorites.value.push(voice);
      await saveFavoritesToStorage();

      ElMessage.success(`已添加 ${voice.name || voice.localName} 到收藏`);
    } catch (err: any) {
      console.error('添加收藏失败:', err);
      ElMessage.error('添加收藏失败');
    }
  };

  /**
   * 从收藏中移除
   * 根据登录状态决定是本地移除还是后端同步
   */
  const removeFromFavorites = async (voiceId: number) => {
    try {
      const index = favorites.value.findIndex(voice => voice.id === voiceId);
      if (index === -1) {
        return; // 未收藏
      }

      const voice = favorites.value[index];
      const userStore = useUserStore();

      // 如果已登录，调用后端API
      if (userStore.isLoggedIn) {
        await favoritesApi.deleteVoice(voiceId);
      }

      // 更新本地状态（无论是否登录都更新）
      favorites.value.splice(index, 1);
      await saveFavoritesToStorage();

      ElMessage.success(`已从收藏中移除 ${voice.name || voice.localName}`);
    } catch (err: any) {
      console.error('移除收藏失败:', err);
      ElMessage.error('移除收藏失败');
    }
  };

  /**
   * 切换收藏状态
   */
  const toggleFavorite = async (voice: VoiceResponse) => {
    if (isFavorite(voice.id)) {
      await removeFromFavorites(voice.id);
    } else {
      await addToFavorites(voice);
    }
  };

  /**
   * 根据shortName获取语音
   */
  const getVoiceByShortName = (shortName: string) => {
    return voiceMap.value.get(shortName);
  };

  /**
   * 获取语言列表
   */
  const getLanguages = () => {
    const languages = new Set<string>();
    voices.value.forEach(voice => {
      languages.add(voice.locale);
    });
    return Array.from(languages).sort();
  };





  /**
   * 获取收藏的语音
   * 如果已登录，从后端获取；否则从本地存储加载
   */
  const fetchFavorites = async () => {
    try {
      const userStore = useUserStore();

      // 如果已登录，从后端获取
      if (userStore.isLoggedIn) {
        const response = await favoritesApi.getVoices();
        favorites.value = response || [];
        await saveFavoritesToStorage();
      } else {
        // 未登录，从本地存储加载
        loadFavoritesFromStorage();
      }
    } catch (err: any) {
      console.error('获取收藏失败:', err);
      error.value = err.message || '获取收藏失败';
      ElMessage({ type: 'error', message: error.value || '获取收藏失败' });
      // 失败时尝试从本地存储加载
      loadFavoritesFromStorage();
    }
  };





  // ============= 缓存相关方法 =============

  const isCacheValid = (languageType: LanguageType) => {
    const cacheKeys = getCacheKeys(languageType);
    const timestamp = webStore.get(cacheKeys.CACHE_TIMESTAMP);
    const version = webStore.get(cacheKeys.CACHE_VERSION);

    if (!timestamp || version !== CACHE_VERSION) {
      return false;
    }

    return Date.now() - timestamp < CACHE_DURATION;
  };

  const getCachedData = (languageType: LanguageType) => {
    const cacheKeys = getCacheKeys(languageType);
    return webStore.get(cacheKeys.VOICES_DATA);
  };

  const cacheData = (languageType: LanguageType, data: any) => {
    const cacheKeys = getCacheKeys(languageType);
    webStore.set(cacheKeys.VOICES_DATA, data);
    webStore.set(cacheKeys.CACHE_TIMESTAMP, Date.now());
    webStore.set(cacheKeys.CACHE_VERSION, CACHE_VERSION);
  };

  const loadFromCache = (cachedData: any) => {
    voices.value = cachedData.voices || [];
    currentTags.value = cachedData.tags || [];
  };

  const saveFavoritesToStorage = async () => {
    webStore.set('user_favorite_voices', favorites.value);
  };

  const loadFavoritesFromStorage = () => {
    const saved = webStore.get('user_favorite_voices');
    if (saved && Array.isArray(saved)) {
      favorites.value = saved;
    }
  };

  /**
   * 统一初始化所有语音相关数据
   */
  const initializeVoiceData = async () => {
    if (loading.value) return;

    try {
      // 首先检查当前语言类型的缓存
      if (voices.value.length === 0 && isCacheValid(currentLanguageType.value)) {
        const cachedData = getCachedData(currentLanguageType.value);
        if (cachedData) {
          loadFromCache(cachedData);
        }
      }

      // 加载语音数据（如果还没有加载）
      if (voices.value.length === 0) {
        await fetchVoices();
      }

    } catch (err: any) {
      console.error('语音数据初始化失败:', err);
      error.value = err.message || '数据初始化失败';
    }
  };

  // ============= 标签管理方法 =============


  // ============= 初始化 =============
  loadFavoritesFromStorage();

  return {
    // 状态
    voices,
    favorites,
    loading,
    error,
    currentLanguageType,
    currentTags,
    selectedTags,
    selectedDialect,
    selectedLocale,
    searchKeyword,

    // 计算属性
    voiceMap,
    favoriteVoiceIds,
    voiceCount,
    favoriteCount,
    filteredVoices,
    availableOptions,
    availableTags,
    getFilterBreadcrumbs,

    // 方法
    isFavorite,
    fetchVoices,
    searchVoices,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    getVoiceByShortName,
    getLanguages,
    fetchFavorites,
    initializeVoiceData,
    fetchVoicesByLanguageType,
    setLanguageType,
    setSelectedTags,
    setSelectedDialect,
    setSelectedLocale,
    setSearchKeyword,
    clearFilters,
    removeFilter,
    getTypeStringFromLanguageType
  };
});
